import { alpha, createTheme } from '@mui/material/styles';

/**
 * Quill AI design system: "refined Material".
 *
 * Warm neutral grounds, white surfaces with 1px borders instead of heavy
 * shadows, one indigo accent (#4F3BD6), Inter type on a 14px base and
 * 8/10/12/16px radii. Values come from the redesign mockups.
 *
 * The same tokens are exposed three ways:
 *  - `lightTheme` / `darkTheme` (MUI), built once at module load;
 *  - `theme.quill.tokens` / `theme.quill.radii` on each theme, for `sx` code
 *    that needs a value the MUI palette has no slot for;
 *  - `--q-*` CSS custom properties. `src/index.css` declares them for
 *    first paint (light on `:root`, dark on `:root[data-theme="dark"]`), and
 *    each theme's CssBaseline re-emits its own set with higher specificity,
 *    so the variables always follow the active MUI theme even if the
 *    `data-theme` attribute is stale. Keep index.css in sync (a unit test in
 *    src/components/brand/__tests__ checks it).
 */

/** localStorage key holding 'light' | 'dark'. Shared with App.js and public/index.html. */
export const THEME_STORAGE_KEY = 'quillai-theme-mode';

/** Corner radii in px. */
export const radii = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  card: 16,
  pill: 999,
};

/** Font stacks. */
export const fonts = {
  sans: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: '"JetBrains Mono", "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", monospace',
};

/**
 * Light tokens (taken from the mockups). Text pairs meet WCAG AA (4.5:1)
 * on `surface` and `bg`; `disabled` is intentionally lower.
 */
const lightTokens = {
  bg: '#F6F5F2',
  bgSubtle: '#FAF9F7',
  surface: '#FFFFFF',
  surfaceRaised: '#FFFFFF',
  subtle: '#F1EFEB',
  hover: '#ECEAE5',
  canvas: '#EEECE7',
  border: '#E3E0DA',
  borderSubtle: '#ECE9E4',
  borderStrong: '#D3CFC7',
  borderHover: '#C9C4BB',
  rowDivider: '#F0EDE8',
  text: '#1C1B19',
  text2: '#3D3A35',
  text3: '#4A463F',
  textDoc: '#2B2925',
  muted: '#5E5A54',
  muted2: '#6B665F',
  faint: '#716C65',
  disabled: '#9C978F',
  accent: '#4F3BD6',
  accentHover: '#3A28B8',
  accentStrong: '#2F2096',
  accentText: '#3A28B8',
  accentSoft: '#ECE8FC',
  accentSelected: '#E7E3FA',
  accentBorder: '#DDD7F7',
  accentTint: '#F7F5FE',
  accentMuted: '#8D86C9',
  onAccent: '#FFFFFF',
  // AI surfaces (AI fields, Inky): one light violet tint, dashed border and icon colour.
  aiTint: '#F9F8FE',
  aiTintStrong: '#F2EFFD',
  aiBorder: '#C9C1F2',
  aiText: '#3A28B8',
  // Neutral selected state for toolbar toggles and list items (purple is reserved for AI).
  controlSelected: '#DCD8D0',
  // Ring on pressed toggles: >= 3:1 against the toolbar and the pressed fill, unlike hover.
  controlSelectedRing: '#7A746B',
  danger: '#D63B1F',
  dangerText: '#9C2C14',
  dangerSoft: '#FCE8E2',
  success: '#1B7F45',
  successText: '#17603A',
  successSoft: '#E3F2E8',
  warning: '#C77700',
  warningText: '#7F4A00',
  warningSoft: '#FDEFD6',
  info: '#2F6FB0',
  infoText: '#1F4E80',
  infoSoft: '#E4EEF8',
  focusRing: '#4F3BD6',
  focusHalo: 'rgba(79, 59, 214, 0.12)',
  selection: 'rgba(79, 59, 214, 0.18)',
  overlay: 'rgba(28, 27, 25, 0.40)',
  tooltipBg: '#1C1B19',
  tooltipText: '#FFFFFF',
  scrollbarThumb: '#D3CFC7',
  scrollbarThumbHover: '#B4AFA7',
  shadowSm: '0 1px 2px rgba(28, 27, 25, 0.05)',
  shadowMd: '0 1px 2px rgba(28, 27, 25, 0.10), 0 2px 8px rgba(28, 27, 25, 0.06)',
  shadowLg: '0 1px 3px rgba(28, 27, 25, 0.10), 0 8px 24px rgba(28, 27, 25, 0.06)',
  shadowXl: '0 4px 14px rgba(28, 27, 25, 0.14), 0 24px 48px rgba(28, 27, 25, 0.12)',
};

/**
 * Dark tokens. Warm near-blacks keep the same character as the light
 * grounds. The accent is lifted to #A497FF so it reads as text (6.9:1 on
 * `surface`), and contained buttons use dark `onAccent` text on it (7.1:1).
 */
const darkTokens = {
  bg: '#141312',
  bgSubtle: '#191816',
  surface: '#1C1B19',
  surfaceRaised: '#23221F',
  subtle: '#2A2825',
  hover: '#2A2926',
  canvas: '#100F0E',
  border: '#34322E',
  borderSubtle: '#2E2C29',
  borderStrong: '#46433E',
  borderHover: '#57534D',
  rowDivider: '#282623',
  text: '#EDEBE7',
  text2: '#D6D3CD',
  text3: '#C9C5BE',
  textDoc: '#E2DFD9',
  muted: '#A8A39B',
  muted2: '#9C978F',
  faint: '#948F87',
  disabled: '#6B665F',
  accent: '#A497FF',
  accentHover: '#B9AFFF',
  accentStrong: '#D9D3FF',
  accentText: '#B9AFFF',
  accentSoft: '#2A2450',
  accentSelected: '#312A63',
  accentBorder: '#3E3577',
  accentTint: '#221E3D',
  accentMuted: '#7D72C9',
  onAccent: '#16123A',
  aiTint: '#1F1B33',
  aiTintStrong: '#272143',
  aiBorder: '#4B4292',
  aiText: '#C9C1FF',
  controlSelected: '#3D3A36',
  controlSelectedRing: '#9C978F',
  danger: '#FF8A70',
  dangerText: '#FFB4A3',
  dangerSoft: '#3A1F19',
  success: '#5CC98A',
  successText: '#8FDDB0',
  successSoft: '#183224',
  warning: '#F0B04A',
  warningText: '#F5C77E',
  warningSoft: '#36280F',
  info: '#7FB2F0',
  infoText: '#B5D3F7',
  infoSoft: '#172636',
  focusRing: '#A497FF',
  focusHalo: 'rgba(164, 151, 255, 0.20)',
  selection: 'rgba(164, 151, 255, 0.30)',
  overlay: 'rgba(0, 0, 0, 0.60)',
  tooltipBg: '#EDEBE7',
  tooltipText: '#1C1B19',
  scrollbarThumb: '#46433E',
  scrollbarThumbHover: '#5E5A54',
  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.30)',
  shadowMd: '0 1px 2px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.25)',
  shadowLg: '0 1px 3px rgba(0, 0, 0, 0.40), 0 8px 24px rgba(0, 0, 0, 0.35)',
  shadowXl: '0 4px 14px rgba(0, 0, 0, 0.45), 0 24px 48px rgba(0, 0, 0, 0.45)',
};

/** Design tokens per mode. Treat as read-only. */
export const designTokens = Object.freeze({
  light: Object.freeze(lightTokens),
  dark: Object.freeze(darkTokens),
});

/**
 * @param {'light'|'dark'} mode Colour mode.
 * @returns {typeof lightTokens} Tokens for the mode.
 */
export function getDesignTokens(mode) {
  return mode === 'dark' ? designTokens.dark : designTokens.light;
}

/**
 * Converts a token key to its CSS custom property name
 * (`accentSoft` -> `--q-accent-soft`, `text2` -> `--q-text-2`).
 * @param {string} key Token key.
 * @returns {string} Custom property name.
 */
export function tokenToCssVar(key) {
  return `--q-${key.replace(/([A-Z])/g, '-$1').replace(/(\d+)/g, '-$1').toLowerCase()}`;
}

/**
 * All `--q-*` custom properties for a mode: colour/shadow tokens, radii and
 * font stacks.
 * @param {'light'|'dark'} mode Colour mode.
 * @returns {Object<string, string>} Map of custom property to value.
 */
export function getCssVariables(mode) {
  const vars = {};
  Object.entries(getDesignTokens(mode)).forEach(([key, value]) => {
    vars[tokenToCssVar(key)] = value;
  });
  Object.entries(radii).forEach(([key, value]) => {
    vars[`--q-radius-${key}`] = `${value}px`;
  });
  vars['--q-font-sans'] = fonts.sans;
  vars['--q-font-mono'] = fonts.mono;
  return vars;
}

/**
 * Reads the persisted theme mode. Defaults to 'light' (matching App.js).
 * @returns {'light'|'dark'} Stored mode.
 */
export function readStoredThemeMode() {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch (error) {
    return 'light';
  }
}

/**
 * Mirrors the active mode onto <html> so plain CSS (index.css, ProseMirror
 * content, third-party widgets) can follow it: sets `data-theme` and the
 * browser `theme-color` meta. `color-scheme` (native form controls and
 * scrollbars) is owned by stylesheets (index.css `:root[data-theme]` and the
 * CssBaseline `html:root` rule), never an inline style, so it always tracks
 * the active MUI theme; any stale inline value is cleared here. Call it
 * whenever the mode changes. Safe outside the browser.
 * @param {'light'|'dark'} mode Colour mode.
 */
export function applyDocumentThemeMode(mode) {
  if (typeof document === 'undefined') return;
  const resolved = mode === 'dark' ? 'dark' : 'light';
  const root = document.documentElement;
  if (root.getAttribute('data-theme') !== resolved) {
    root.setAttribute('data-theme', resolved);
  }
  if (root.style && root.style.colorScheme) root.style.removeProperty('color-scheme');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', getDesignTokens(resolved).bg);
}

/**
 * QA-016 (WCAG 2.4.7): visible keyboard focus indicator.
 *
 * Returned as a style object for a `&:focus-visible` rule so that pointer
 * interactions never paint a ring. The colour comes from the active theme's
 * primary colour, which is tuned per mode to contrast with its surfaces.
 *
 * @param {import('@mui/material/styles').Theme} theme Active theme.
 * @param {string} [offset] `outline-offset`. Use a negative value for controls
 *   rendered inside clipping containers so the ring is not cropped.
 * @returns {object} Style object.
 */
const focusRing = (theme, offset = '2px') => ({
  outline: `2px solid ${theme.palette.primary.main}`,
  outlineOffset: offset,
  // Keeps the ring above adjacent surfaces that would otherwise paint over it.
  zIndex: 1,
});

/** px -> rem against the 16px root. */
const rem = (px) => `${px / 16}rem`;

const typography = {
  fontFamily: fonts.sans,
  fontSize: 14,
  htmlFontSize: 16,
  fontWeightLight: 400,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontSize: rem(34), fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em' },
  h2: { fontSize: rem(30), fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h3: { fontSize: rem(28), fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h4: { fontSize: rem(24), fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.02em' },
  h5: { fontSize: rem(20), fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },
  h6: { fontSize: rem(16), fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.01em' },
  subtitle1: { fontSize: rem(15), fontWeight: 500, lineHeight: 1.45, letterSpacing: 0 },
  subtitle2: { fontSize: rem(13.5), fontWeight: 600, lineHeight: 1.45, letterSpacing: 0 },
  body1: { fontSize: rem(14), fontWeight: 400, lineHeight: 1.5, letterSpacing: 0 },
  body2: { fontSize: rem(13), fontWeight: 400, lineHeight: 1.45, letterSpacing: 0 },
  caption: { fontSize: rem(12), fontWeight: 400, lineHeight: 1.4, letterSpacing: 0 },
  overline: { fontSize: rem(11.5), fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.06em', textTransform: 'uppercase' },
  button: { fontSize: rem(13.5), fontWeight: 600, lineHeight: 1.25, letterSpacing: 0, textTransform: 'none' },
};

/** Warm neutral scale (absolute; identical in both modes). */
const grey = {
  50: '#FAF9F7',
  100: '#F1EFEB',
  200: '#E8E5DF',
  300: '#D3CFC7',
  400: '#B4AFA7',
  500: '#9C978F',
  600: '#77726B',
  700: '#5E5A54',
  800: '#3D3A35',
  900: '#1C1B19',
  A100: '#F1EFEB',
  A200: '#ECE9E4',
  A400: '#B4AFA7',
  A700: '#5E5A54',
};

/**
 * MUI palette for a mode.
 * @param {'light'|'dark'} mode Colour mode.
 * @param {typeof lightTokens} t Tokens.
 * @returns {object} Palette options.
 */
function buildPalette(mode, t) {
  const isDark = mode === 'dark';
  const ink = isDark ? '237, 235, 231' : '28, 27, 25';
  return {
    mode,
    primary: {
      main: t.accent,
      light: isDark ? '#C4BBFF' : '#7A68EC',
      dark: t.accentHover,
      contrastText: t.onAccent,
    },
    secondary: isDark
      ? { main: '#F2957A', light: '#F7B6A2', dark: '#DB7457', contrastText: '#1C1B19' }
      : { main: '#C24E2A', light: '#DA6A45', dark: '#9C3A1C', contrastText: '#FFFFFF' },
    error: isDark
      ? { main: t.danger, light: t.dangerText, dark: '#E5664B', contrastText: '#1C1B19' }
      : { main: '#C8371D', light: t.danger, dark: t.dangerText, contrastText: '#FFFFFF' },
    warning: isDark
      ? { main: t.warning, light: t.warningText, dark: '#D9952A', contrastText: '#1C1B19' }
      : { main: '#A05E00', light: t.warning, dark: t.warningText, contrastText: '#FFFFFF' },
    success: isDark
      ? { main: t.success, light: t.successText, dark: '#3FAF70', contrastText: '#0F1F16' }
      : { main: t.success, light: '#1F8A4C', dark: t.successText, contrastText: '#FFFFFF' },
    info: isDark
      ? { main: t.info, light: t.infoText, dark: '#5A93D6', contrastText: '#0E1A26' }
      : { main: t.info, light: '#4B88C7', dark: t.infoText, contrastText: '#FFFFFF' },
    grey,
    background: {
      default: t.bg,
      paper: t.surface,
    },
    text: {
      primary: t.text,
      secondary: t.muted,
      disabled: t.disabled,
    },
    divider: t.border,
    action: {
      active: t.muted,
      hover: `rgba(${ink}, ${isDark ? 0.07 : 0.05})`,
      hoverOpacity: isDark ? 0.07 : 0.05,
      selected: alpha(t.accent, isDark ? 0.18 : 0.1),
      selectedOpacity: isDark ? 0.18 : 0.1,
      focus: `rgba(${ink}, 0.12)`,
      focusOpacity: 0.12,
      disabled: `rgba(${ink}, ${isDark ? 0.36 : 0.32})`,
      disabledBackground: `rgba(${ink}, ${isDark ? 0.12 : 0.08})`,
      disabledOpacity: 0.38,
      activatedOpacity: 0.12,
    },
  };
}

/**
 * 25-step elevation scale. Kept deliberately soft: surfaces are separated by
 * borders, shadows only lift floating layers (menus at 8, dialogs at 24).
 * @param {typeof lightTokens} t Tokens.
 * @returns {string[]} Shadows.
 */
function buildShadows(t) {
  return Array.from({ length: 25 }, (_, level) => {
    if (level === 0) return 'none';
    if (level === 1) return t.shadowSm;
    if (level <= 4) return t.shadowMd;
    if (level <= 12) return t.shadowLg;
    return t.shadowXl;
  });
}

/** Soft colour pairs used by filled chips and standard alerts. */
const softColors = (t) => ({
  primary: { bg: t.accentSoft, fg: t.accentStrong, border: t.accentBorder, icon: t.accent },
  secondary: { bg: t.dangerSoft, fg: t.dangerText, border: alpha(t.danger, 0.25), icon: t.danger },
  error: { bg: t.dangerSoft, fg: t.dangerText, border: alpha(t.danger, 0.25), icon: t.danger },
  success: { bg: t.successSoft, fg: t.successText, border: alpha(t.success, 0.25), icon: t.success },
  warning: { bg: t.warningSoft, fg: t.warningText, border: alpha(t.warning, 0.3), icon: t.warning },
  info: { bg: t.infoSoft, fg: t.infoText, border: alpha(t.info, 0.25), icon: t.info },
});

/**
 * Component overrides for a mode.
 * @param {'light'|'dark'} mode Colour mode.
 * @param {typeof lightTokens} t Tokens.
 * @returns {import('@mui/material/styles').Components} Overrides.
 */
function buildComponents(mode, t) {
  const isDark = mode === 'dark';
  const soft = softColors(t);
  const easing = '120ms cubic-bezier(0.2, 0, 0, 1)';
  const transition = ['background-color', 'border-color', 'color', 'box-shadow'].map((p) => `${p} ${easing}`).join(', ');
  const floatingPaper = {
    backgroundColor: t.surfaceRaised,
    backgroundImage: 'none',
    border: `1px solid ${t.border}`,
    borderRadius: radii.xl,
    boxShadow: t.shadowLg,
  };
  const vars = getCssVariables(mode);

  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Re-emit the --q-* variables for the active theme. `html:root` and
        // `html:root[data-theme]` outrank index.css's `:root` and
        // `:root[data-theme="dark"]`, so the variables follow the MUI theme
        // even before App mirrors the mode onto <html>.
        'html:root, html:root[data-theme]': {
          ...vars,
          colorScheme: mode,
        },
        html: {
          // QA-036: always reserve the scrollbar gutter so that removing the
          // body scrollbar (Dialog/Modal scroll lock) cannot reflow the page.
          scrollbarGutter: 'stable',
          WebkitTextSizeAdjust: '100%',
        },
        body: {
          fontFamily: fonts.sans,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        '::selection': {
          backgroundColor: t.selection,
        },
        // The scroll lock also adds an inline `padding-right: <scrollbar>px`
        // to <body> to compensate for the scrollbar it hides. With a stable
        // gutter that space is never reclaimed, so the compensation
        // double-counts and shifts content the other way. Cancel it only
        // where the gutter is actually supported.
        '@supports (scrollbar-gutter: stable)': {
          body: {
            paddingRight: '0 !important',
          },
        },
      },
    },

    // QA-016: ButtonBase backs Button, IconButton, Fab, Tab, MenuItem,
    // ListItemButton, ToggleButton and the selection controls, so one override
    // gives the whole app a focus ring. It also overrides ButtonBase's own
    // `outline: 0`.
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:focus-visible': focusRing(theme),
        }),
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          fontWeight: 600,
          letterSpacing: 0,
          textTransform: 'none',
          transition,
          whiteSpace: 'nowrap',
        },
        sizeSmall: { minHeight: 32, fontSize: rem(13) },
        sizeMedium: { minHeight: 36, fontSize: rem(13.5) },
        sizeLarge: { minHeight: 40, fontSize: rem(14) },
        containedSizeSmall: { padding: '5px 12px' },
        containedSizeMedium: { padding: '7px 14px' },
        containedSizeLarge: { padding: '9px 18px' },
        outlinedSizeSmall: { padding: '4px 11px' },
        outlinedSizeMedium: { padding: '6px 13px' },
        outlinedSizeLarge: { padding: '8px 17px' },
        textSizeSmall: { padding: '5px 8px' },
        textSizeMedium: { padding: '7px 10px' },
        containedPrimary: {
          '&:hover': { backgroundColor: t.accentHover },
        },
        // The mockups use neutral outlined buttons for secondary actions;
        // only the primary colour is neutralised so error/success outlines
        // keep their meaning.
        outlinedPrimary: {
          color: t.text,
          borderColor: t.borderStrong,
          backgroundColor: t.surface,
          '&:hover': {
            borderColor: t.borderHover,
            backgroundColor: t.hover,
          },
        },
        outlinedInherit: {
          borderColor: t.borderStrong,
        },
        textPrimary: {
          color: isDark ? t.accent : t.accentText,
          '&:hover': { backgroundColor: t.accentSoft },
        },
        startIcon: { marginRight: 6 },
        endIcon: { marginLeft: 6 },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          transition,
        },
        sizeSmall: { padding: 6 },
        sizeMedium: { padding: 7 },
      },
    },

    MuiFab: {
      styleOverrides: {
        root: { boxShadow: t.shadowMd, textTransform: 'none' },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: radii.md,
          borderColor: t.borderStrong,
          color: t.text2,
          fontWeight: 500,
          fontSize: rem(13),
          padding: '6px 12px',
          transition,
          '&:hover': { backgroundColor: t.hover },
          '&.Mui-selected': {
            backgroundColor: t.accentSoft,
            color: t.accentStrong,
            '&:hover': { backgroundColor: t.accentSelected },
          },
        },
        sizeSmall: { padding: '4px 10px', fontSize: rem(12.5) },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          backgroundColor: t.surface,
          transition,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.borderHover,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${t.focusHalo}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.accent,
            borderWidth: 1.5,
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(t.danger, 0.15)}`,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? t.danger : '#C8371D',
          },
          '&.Mui-disabled': {
            backgroundColor: t.bgSubtle,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: t.borderSubtle,
          },
        },
        notchedOutline: {
          borderColor: t.border,
        },
        input: {
          '&::placeholder': { color: t.faint, opacity: 1 },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderTopLeftRadius: radii.lg,
          borderTopRightRadius: radii.lg,
          backgroundColor: t.subtle,
          '&:hover': { backgroundColor: t.hover },
          '&.Mui-focused': { backgroundColor: t.subtle },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: rem(14),
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: t.muted,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: rem(12), marginTop: 4 },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: t.muted },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { ...floatingPaper, marginTop: 4 },
        listbox: {
          padding: 6,
          '& .MuiAutocomplete-option': {
            borderRadius: radii.md,
            fontSize: rem(13.5),
            minHeight: 34,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: t.accentSoft,
          },
        },
        tag: { margin: 2 },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const color = ownerState.color && ownerState.color !== 'default' ? soft[ownerState.color] : null;
          const filled = ownerState.variant !== 'outlined';
          const base = {
            borderRadius: radii.pill,
            fontWeight: 500,
            fontSize: rem(12),
            height: 26,
            transition,
            // Chip is not a ButtonBase, so it needs its own ring.
            '&:focus-visible': focusRing(theme),
          };
          if (filled && color) {
            // Soft tonal chips (mockup status pills) instead of solid fills.
            return {
              ...base,
              backgroundColor: color.bg,
              color: color.fg,
              '& .MuiChip-icon': { color: color.icon },
              '& .MuiChip-deleteIcon': { color: alpha(color.fg, 0.6), '&:hover': { color: color.fg } },
              '&.MuiChip-clickable:hover, &.Mui-focusVisible': { backgroundColor: alpha(color.icon, isDark ? 0.28 : 0.16) },
            };
          }
          if (filled) {
            return {
              ...base,
              backgroundColor: t.subtle,
              color: t.text3,
              '&.MuiChip-clickable:hover, &.Mui-focusVisible': { backgroundColor: t.hover },
            };
          }
          if (!color) {
            return {
              ...base,
              borderColor: t.borderStrong,
              color: t.text2,
              '&.MuiChip-clickable:hover': { backgroundColor: t.hover },
            };
          }
          return base;
        },
        sizeSmall: { height: 22, fontSize: rem(11.5) },
        label: { paddingLeft: 10, paddingRight: 10 },
        labelSmall: { paddingLeft: 8, paddingRight: 8 },
        icon: { marginLeft: 6, marginRight: -4, fontSize: 16 },
        deleteIcon: { fontSize: 16, marginRight: 5 },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          // MUI's dark-mode elevation overlay fights the flat surfaces.
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: radii.xl,
        },
        outlined: {
          borderColor: t.border,
        },
        elevation1: {
          border: `1px solid ${t.border}`,
          boxShadow: t.shadowSm,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          border: `1px solid ${t.border}`,
          boxShadow: 'none',
          backgroundImage: 'none',
          transition,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '16px 20px' },
        title: { fontSize: rem(15), fontWeight: 600 },
        subheader: { fontSize: rem(13), color: t.muted },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: { padding: '12px 20px' },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': { display: 'none' },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.card,
          boxShadow: t.shadowXl,
          backgroundImage: 'none',
          border: isDark ? `1px solid ${t.border}` : 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: rem(17),
          fontWeight: 600,
          lineHeight: 1.35,
          letterSpacing: '-0.01em',
          padding: '20px 24px 12px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        dividers: { borderColor: t.borderSubtle },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: 4 },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ ownerState }) => (ownerState.invisible ? {} : { backgroundColor: t.overlay }),
      },
    },

    // QA-036: menus and popovers have no reason to lock the page scroll, and
    // locking it is what removes the scrollbar and shifts the layout.
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: { ...floatingPaper, minWidth: 180 },
        list: { padding: 6 },
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: floatingPaper,
      },
    },
    // Controls inside clipping containers need an inset ring: the Popover
    // paper uses `overflow-x: hidden` and the Tabs scroller uses
    // `overflow: hidden`, both of which would crop an outset outline. The
    // doubled `&&` outranks the MuiButtonBase rule regardless of order.
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&&:focus-visible': focusRing(theme, '-2px'),
          borderRadius: radii.md,
          fontSize: rem(13.5),
          minHeight: 34,
          paddingLeft: 10,
          paddingRight: 10,
          '& + &': { marginTop: 1 },
          '&:hover': { backgroundColor: t.hover },
          '&.Mui-selected': {
            backgroundColor: t.accentSoft,
            color: t.accentStrong,
            '&:hover': { backgroundColor: t.accentSelected },
          },
          '& .MuiListItemIcon-root': { minWidth: 30, color: t.muted },
          '&.Mui-selected .MuiListItemIcon-root': { color: 'inherit' },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: t.tooltipBg,
          color: t.tooltipText,
          fontSize: rem(12),
          fontWeight: 500,
          lineHeight: 1.4,
          borderRadius: radii.sm,
          padding: '6px 10px',
          boxShadow: t.shadowMd,
        },
        arrow: {
          color: t.tooltipBg,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          backgroundColor: t.tooltipBg,
          color: t.tooltipText,
          boxShadow: t.shadowLg,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
          backgroundColor: t.accent,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&&:focus-visible': focusRing(theme, '-2px'),
          textTransform: 'none',
          minHeight: 44,
          minWidth: 0,
          padding: '10px 14px',
          fontSize: rem(13.5),
          fontWeight: 500,
          letterSpacing: 0,
          color: t.muted,
          transition,
          '&:hover': { color: t.text },
          '&.Mui-selected': { color: t.text },
        }),
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 'inherit' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${t.borderSubtle}`,
          padding: '10px 12px',
          fontSize: rem(13.5),
        },
        head: {
          fontSize: rem(12),
          fontWeight: 500,
          lineHeight: 1.4,
          color: t.muted,
          padding: '9px 12px',
          borderBottom: `1px solid ${t.border}`,
          whiteSpace: 'nowrap',
        },
        stickyHeader: {
          backgroundColor: t.surface,
        },
        sizeSmall: {
          padding: '6px 10px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: t.bgSubtle },
          '&.Mui-selected': { backgroundColor: t.accentTint },
          '&.Mui-selected:hover': { backgroundColor: t.accentSoft },
          '&:last-child > .MuiTableCell-body': { borderBottom: 0 },
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: alpha(t.bg, 0.88),
          backgroundImage: 'none',
          color: t.text,
          backdropFilter: 'saturate(180%) blur(10px)',
          borderBottom: `1px solid ${t.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: t.bg,
          backgroundImage: 'none',
          borderColor: t.border,
        },
      },
    },

    MuiList: {
      styleOverrides: {
        padding: { paddingTop: 6, paddingBottom: 6 },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          color: t.faint,
          fontSize: rem(11.5),
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          lineHeight: '32px',
        },
      },
    },
    // Sidebar navigation style (Main mockup): pill rows, soft-accent
    // selection. Inside a Drawer or <nav> the rows become 36px pills; other
    // lists keep an 8px radius. The ancestor test sits inside :where() so the
    // nav rule keeps the button's own 0,1,0 specificity: theme overrides are
    // emitted before sx in the same class, so caller sx (e.g. nested items'
    // pl: 4) still wins. Rule order matters: the selected rules come last so
    // they win over the nav defaults.
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&&:focus-visible': focusRing(theme, '-2px'),
          borderRadius: radii.md,
          transition,
          ':where(.MuiDrawer-paper, nav) &': {
            borderRadius: radii.pill,
            minHeight: 36,
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 12,
            paddingRight: 12,
            color: t.text2,
          },
          ':where(.MuiDrawer-paper, nav) & .MuiListItemText-primary': {
            fontSize: rem(14),
            fontWeight: 500,
          },
          '&:hover': { backgroundColor: t.hover },
          '&.Mui-selected': {
            backgroundColor: t.accentSelected,
            color: t.accentStrong,
          },
          '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: t.accentSelected,
          },
          '&.Mui-selected .MuiListItemIcon-root': { color: 'inherit' },
          '&.Mui-selected .MuiListItemText-primary': { fontWeight: 600 },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 34, color: t.muted },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        secondary: { fontSize: rem(12.5), color: t.muted },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          fontSize: rem(13.5),
          alignItems: 'flex-start',
          padding: '8px 14px',
        },
        icon: { paddingTop: 8, marginRight: 10, opacity: 1 },
        message: { paddingTop: 9, paddingBottom: 8, lineHeight: 1.45 },
        action: { paddingTop: 2 },
        ...['success', 'info', 'warning', 'error'].reduce((acc, key) => {
          const s = soft[key];
          const cap = key.charAt(0).toUpperCase() + key.slice(1);
          acc[`standard${cap}`] = {
            backgroundColor: s.bg,
            color: s.fg,
            border: `1px solid ${s.border}`,
            '& .MuiAlert-icon': { color: s.icon },
          };
          acc[`outlined${cap}`] = {
            color: s.fg,
            borderColor: s.border,
            '& .MuiAlert-icon': { color: s.icon },
          };
          return acc;
        }, {}),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: rem(13.5), marginBottom: 2 },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: radii.pill,
          backgroundColor: t.subtle,
        },
        bar: {
          borderRadius: radii.pill,
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: '#FFFFFF' },
          '&.Mui-checked + .MuiSwitch-track': { opacity: 1 },
        },
        thumb: {
          boxShadow: '0 1px 2px rgba(28, 27, 25, 0.30)',
        },
        track: {
          opacity: 1,
          borderRadius: radii.pill,
          backgroundColor: t.borderStrong,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        // Unchecked outline needs 3:1 non-text contrast (WCAG 1.4.11) on
        // surfaces; faint is >= 4.5:1 on both grounds in both modes.
        root: { color: t.faint },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { color: t.faint },
      },
    },

    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '&:focus-visible': focusRing(theme),
          borderRadius: 2,
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: t.border },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: rem(13), fontWeight: 600 },
        // Only the background is themed: MUI's default text colour
        // (palette.background.default) stays, so callers that set just
        // sx={{ bgcolor: 'primary.main' | 'secondary.main' }} keep readable
        // initials (light ground on dark fills in light mode, and the reverse
        // in dark mode). Neutral fills are chosen to keep >= 4.5:1 with it.
        colorDefault: { backgroundColor: isDark ? grey[400] : grey[700] },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 600, fontSize: rem(11) },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: t.subtle },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: rem(13), color: t.muted },
        separator: { color: t.disabled },
      },
    },
  };
}

/**
 * Builds a full theme for a mode. Called once per mode at module load; never
 * per render.
 * @param {'light'|'dark'} mode Colour mode.
 * @returns {import('@mui/material/styles').Theme} Theme.
 */
function buildTheme(mode) {
  const t = getDesignTokens(mode);
  return createTheme({
    palette: buildPalette(mode, t),
    typography,
    shape: {
      // Kept at 4 so existing `sx={{ borderRadius: 2 }}` style multipliers
      // keep resolving to the same pixel values (2 -> 8px, 3 -> 12px,
      // 4 -> 16px). Component radii come from `radii`.
      borderRadius: 4,
    },
    shadows: buildShadows(t),
    components: buildComponents(mode, t),
    quill: {
      mode,
      tokens: t,
      radii,
      fonts,
    },
  });
}

/**
 * Light theme configuration.
 * @type {import('@mui/material/styles').Theme}
 */
export const lightTheme = buildTheme('light');

/**
 * Dark theme configuration.
 * @type {import('@mui/material/styles').Theme}
 */
export const darkTheme = buildTheme('dark');

export default lightTheme;
